import type { FixedWingManageLine, FixedWingSavedLine } from '../models/fixed-wing.models';

/** Client-owned defaults. The scheduler never seeds these. */
export const FIXED_WING_DEFAULT_LINE_COUNT = 6;

export function defaultFixedWingLineName(order: number): string {
  return `Line ${order}`;
}

/** Stable select value. Unsaved defaults have no id, so they cannot share the saved-line key space. */
export function fixedWingManageLineKey(line: Pick<FixedWingManageLine, 'id' | 'order'>): string {
  return line.id === null ? `default:${line.order}` : `saved:${line.id}`;
}

/**
 * Saved lines win on `order`. Each of orders 1–6 missing from `saved` is filled with
 * an unsaved "Line N" row. A saved line at order 3 replaces Line 3 whatever its name is.
 */
export function mergeFixedWingManageLines(
  date: string,
  saved: readonly FixedWingSavedLine[]
): FixedWingManageLine[] {
  const occupiedOrders = new Set(saved.map((line) => line.order));
  const defaults: FixedWingManageLine[] = [];
  for (let order = 1; order <= FIXED_WING_DEFAULT_LINE_COUNT; order++) {
    if (occupiedOrders.has(order)) {
      continue;
    }
    defaults.push({
      id: null,
      date,
      name: defaultFixedWingLineName(order),
      order,
      stage: null,
      active: true,
      locked: false,
      isDefault: true,
    });
  }
  const savedRows: FixedWingManageLine[] = saved.map((line) => ({
    id: line.id,
    date: line.date,
    name: line.name,
    order: line.order,
    stage: line.stage,
    active: line.active,
    locked: line.locked,
    isDefault: line.isDefault,
  }));
  return [...savedRows, ...defaults].sort((a, b) => a.order - b.order);
}

/** Next custom-line order: one past the highest order in the merged list (7 when only 1–6 exist). */
export function nextFixedWingLineOrder(lines: readonly { order: number }[]): number {
  return lines.reduce((max, line) => Math.max(max, line.order), 0) + 1;
}
